import { useAuth } from "@/contexts/AuthContext";
import supabase from "@/services/supabaseClient";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { ProfileTabView } from "./ProfileTabView";

export const ProfileTab: React.FC = () => {
  const { user, updatePassword } = useAuth();
  const [name, setName] = useState("");
  // Last value known to be persisted; Cancel restores the form to it.
  const [savedName, setSavedName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // The password fields are always visible, so intent to change is inferred from them being
  // filled in — an untouched pair means the user is only editing their display name.
  const isChangingPassword = password.length > 0 || confirmPassword.length > 0;
  const isPasswordTooShort = password.length > 0 && password.length < 6;
  const doesPasswordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  useEffect(() => {
    if (!user) return;
    setEmail(user.email || "");

    const fetchUserSettings = async () => {
      const { data, error } = await supabase
        .from("public_users")
        .select("name")
        .eq("user_id", user.id)
        .single();

      if (error) {
        console.error("Error fetching user settings:", error);
      } else if (data) {
        setName(data.name || "");
        setSavedName(data.name || "");
      }
    };

    void fetchUserSettings();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Name cannot be empty");
      return;
    }
    setIsSaving(true);

    try {
      const { error: settingsError } = await supabase.from("public_users").upsert(
        {
          user_id: user?.id,
          name: trimmedName,
        },
        { onConflict: "user_id" }
      );

      if (settingsError) throw settingsError;
      setName(trimmedName);
      setSavedName(trimmedName);

      if (isChangingPassword) {
        if (!password) {
          toast.error("Please enter a new password");
          setIsSaving(false);
          return;
        }
        if (doesPasswordMismatch) {
          toast.error("Passwords do not match");
          setIsSaving(false);
          return;
        }
        if (isPasswordTooShort) {
          toast.error("Password must be at least 6 characters");
          setIsSaving(false);
          return;
        }

        await updatePassword(password);
        toast.success("Password updated successfully");
        setPassword("");
        setConfirmPassword("");
      }

      toast.success("Settings updated successfully");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred while saving";
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setName(savedName);
    setPassword("");
    setConfirmPassword("");
  };

  return (
    <ProfileTabView
      name={name}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      isSaving={isSaving}
      isPasswordTooShort={isPasswordTooShort}
      doesPasswordMismatch={doesPasswordMismatch}
      setName={setName}
      setPassword={setPassword}
      setConfirmPassword={setConfirmPassword}
      onSubmit={handleSubmit}
      onReset={handleReset}
    />
  );
};
